import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Bell, BellOff, Smartphone, Monitor, Loader2, Send, 
  Settings, CheckCircle, XCircle, RefreshCw, Trash2,
  Volume2, VolumeX, Zap, Users, History
} from "lucide-react";

const NOTIFICATION_CATEGORIES = [
  { id: "alertas", label: "Alertas del Sistema", description: "Alertas automáticas configuradas" },
  { id: "mensajeria", label: "Mensajería", description: "Nuevas solicitudes y entregas" },
  { id: "tickets", label: "Mesa de Ayuda", description: "Tickets nuevos y actualizaciones" },
  { id: "visitas", label: "Visitantes", description: "Visitas programadas y alertas" },
  { id: "pagos", label: "Pagos y Facturas", description: "Vencimientos y recordatorios" },
  { id: "inventario", label: "Inventario", description: "Stock bajo y alertas" }
];

export default function NotificacionesPushPage() {
  const [status, setStatus] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [showBroadcastDialog, setShowBroadcastDialog] = useState(false);
  const [testMessage, setTestMessage] = useState({ title: "Prueba de Notificación", body: "Esta es una notificación de prueba" });
  const [broadcastMessage, setBroadcastMessage] = useState({ title: "", body: "", url: "/" });
  const [adminStats, setAdminStats] = useState(null);
  const [usuariosEstado, setUsuariosEstado] = useState([]);
  const [testingUserId, setTestingUserId] = useState(null);
  const [preferences, setPreferences] = useState({
    alerts_enabled: true,
    sound_enabled: true,
    categories: {}
  });

  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes(user?.role?.toLowerCase());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statusRes, subsRes] = await Promise.all([
        axios.get(`${API}/push-notifications/status`, getAuthHeaders()),
        axios.get(`${API}/push-notifications/subscriptions`, getAuthHeaders())
      ]);
      
      setStatus(statusRes.data);
      setPreferences(statusRes.data.preferences || preferences);
      setSubscriptions(subsRes.data.items || []);
      
      if (isAdmin) {
        const [statsRes, usersRes] = await Promise.all([
          axios.get(`${API}/push-notifications/admin/stats`, getAuthHeaders()),
          axios.get(`${API}/push-notifications/admin/usuarios`, getAuthHeaders())
        ]);
        setAdminStats(statsRes.data);
        setUsuariosEstado(usersRes.data.usuarios || []);
      }
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Check if push notifications are supported
  const isPushSupported = () => {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  };

  // Get current permission status
  const getPermissionStatus = () => {
    if (!isPushSupported()) return 'unsupported';
    return typeof Notification !== 'undefined' ? Notification.permission : 'default';
  };

  // Request notification permission and subscribe
  const handleSubscribe = async () => {
    if (!isPushSupported()) {
      toast.error("Las notificaciones push no son compatibles con este navegador");
      return;
    }

    setSubscribing(true);
    try {
      if (typeof Notification === 'undefined') {
        toast.error("Las notificaciones no son compatibles con este navegador");
        setSubscribing(false);
        return;
      }
      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error("Permiso de notificaciones denegado");
        setSubscribing(false);
        return;
      }

      // Get VAPID public key
      const keyRes = await axios.get(`${API}/push-notifications/vapid-key`);
      if (!keyRes.data.configured) {
        toast.error("Push notifications no configuradas en el servidor");
        setSubscribing(false);
        return;
      }

      // Register service worker if not already registered
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyRes.data.publicKey)
      });

      // Get device info
      const deviceName = getDeviceName();
      const deviceType = getDeviceType();

      // Send subscription to server
      await axios.post(`${API}/push-notifications/subscribe`, {
        subscription: subscription.toJSON(),
        device_name: deviceName,
        device_type: deviceType
      }, getAuthHeaders());

      toast.success("¡Notificaciones activadas!");
      fetchData();
    } catch (err) {
      console.error("Subscription error:", err);
      toast.error("Error al activar notificaciones");
    } finally {
      setSubscribing(false);
    }
  };

  // Unsubscribe from push notifications
  const handleUnsubscribe = async (endpoint) => {
    try {
      await axios.delete(`${API}/push-notifications/unsubscribe?endpoint=${encodeURIComponent(endpoint)}`, getAuthHeaders());
      toast.success("Dispositivo eliminado");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar dispositivo");
    }
  };

  // Update preferences
  const handleUpdatePreferences = async (newPrefs) => {
    try {
      setPreferences(newPrefs);
      await axios.put(`${API}/push-notifications/preferences`, newPrefs, getAuthHeaders());
      toast.success("Preferencias actualizadas");
    } catch (err) {
      toast.error("Error al actualizar preferencias");
    }
  };

  // Toggle category
  const toggleCategory = (categoryId) => {
    const newCategories = {
      ...preferences.categories,
      [categoryId]: !preferences.categories[categoryId]
    };
    handleUpdatePreferences({ ...preferences, categories: newCategories });
  };

  // Send test notification
  const handleSendTest = async () => {
    try {
      await axios.post(`${API}/push-notifications/send`, {
        title: testMessage.title,
        body: testMessage.body,
        url: "/"
      }, getAuthHeaders());
      toast.success("Notificación de prueba enviada");
      setShowTestDialog(false);
    } catch (err) {
      toast.error("Error al enviar notificación");
    }
  };

  // Send broadcast (admin)
  const handleTestUsuario = async (u) => {
    try {
      setTestingUserId(u.id);
      const res = await axios.post(`${API}/push-notifications/admin/test/${u.id}`, {}, getAuthHeaders());
      if (res.data.sent_count > 0) toast.success(res.data.message);
      else toast.warning(res.data.message);
    } catch (err) {
      toast.error("Error al enviar la prueba");
    } finally {
      setTestingUserId(null);
    }
  };

  const handleBroadcast = async () => {
    try {
      const res = await axios.post(`${API}/push-notifications/broadcast`, {
        payload: {
          title: broadcastMessage.title,
          body: broadcastMessage.body,
          url: broadcastMessage.url || "/"
        }
      }, getAuthHeaders());
      toast.success(res.data.message);
      setShowBroadcastDialog(false);
      setBroadcastMessage({ title: "", body: "", url: "/" });
      fetchData();
    } catch (err) {
      toast.error("Error al enviar broadcast");
    }
  };

  // Helper functions
  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  function getDeviceName() {
    const ua = navigator.userAgent;
    if (ua.includes('iPhone')) return 'iPhone';
    if (ua.includes('iPad')) return 'iPad';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('Windows')) return 'Windows PC';
    if (ua.includes('Mac')) return 'Mac';
    return 'Navegador Web';
  }

  function getDeviceType() {
    const ua = navigator.userAgent;
    if (/Mobi|Android/i.test(ua)) return 'mobile';
    return 'desktop';
  }

  const permissionStatus = getPermissionStatus();

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
        <div className="max-w-4xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Bell className="w-7 h-7 text-blue-500" />
                Notificaciones Push
              </h1>
              <p className="text-slate-500 mt-1">Configura las notificaciones en tiempo real para tu dispositivo</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={fetchData} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
              {status?.subscribed && (
                <Button variant="outline" onClick={() => setShowTestDialog(true)}>
                  <Send className="w-4 h-4 mr-2" />
                  Probar
                </Button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : (
            <Tabs defaultValue="dispositivos" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="dispositivos">Mis Dispositivos</TabsTrigger>
                <TabsTrigger value="preferencias">Preferencias</TabsTrigger>
                {isAdmin && <TabsTrigger value="admin">Administración</TabsTrigger>}
              </TabsList>

              {/* Devices Tab */}
              <TabsContent value="dispositivos">
                {/* Subscription Status Card */}
                <Card className="mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center ${status?.subscribed ? 'bg-green-100' : 'bg-slate-100'}`}>
                          {status?.subscribed ? (
                            <Bell className="w-8 h-8 text-green-600" />
                          ) : (
                            <BellOff className="w-8 h-8 text-slate-400" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold">
                            {status?.subscribed ? "Notificaciones Activas" : "Notificaciones Desactivadas"}
                          </h3>
                          <p className="text-slate-500">
                            {status?.subscribed 
                              ? `${status.subscription_count} dispositivo(s) registrado(s)`
                              : "Activa las notificaciones para recibir alertas en tiempo real"}
                          </p>
                          {!isPushSupported() && (
                            <Badge variant="destructive" className="mt-2">
                              Navegador no compatible
                            </Badge>
                          )}
                          {permissionStatus === 'denied' && (
                            <Badge variant="destructive" className="mt-2">
                              Permiso denegado - habilita en configuración del navegador
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {!status?.subscribed && isPushSupported() && permissionStatus !== 'denied' && (
                        <Button 
                          onClick={handleSubscribe} 
                          disabled={subscribing}
                          className="bg-[#0C314C] text-white"
                        >
                          {subscribing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
                          Activar Notificaciones
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Subscribed Devices */}
                {subscriptions.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Dispositivos Registrados</CardTitle>
                      <CardDescription>Dispositivos que recibirán notificaciones push</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {subscriptions.map((sub, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              {sub.device_type === 'mobile' ? (
                                <Smartphone className="w-5 h-5 text-slate-500" />
                              ) : (
                                <Monitor className="w-5 h-5 text-slate-500" />
                              )}
                              <div>
                                <p className="font-medium">{sub.device_name}</p>
                                <p className="text-sm text-slate-500">
                                  Registrado: {new Date(sub.created_at).toLocaleDateString('es-DO')}
                                </p>
                              </div>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500"
                              onClick={() => handleUnsubscribe(sub.endpoint)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      
                      {/* Add another device */}
                      <Button 
                        variant="outline" 
                        className="w-full mt-4"
                        onClick={handleSubscribe}
                        disabled={subscribing}
                      >
                        {subscribing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Smartphone className="w-4 h-4 mr-2" />}
                        Agregar este dispositivo
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Preferences Tab */}
              <TabsContent value="preferencias">
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-lg">Configuración General</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Bell className="w-5 h-5 text-slate-500" />
                        <div>
                          <Label className="font-medium">Alertas Habilitadas</Label>
                          <p className="text-sm text-slate-500">Recibir notificaciones push</p>
                        </div>
                      </div>
                      <Switch 
                        checked={preferences.alerts_enabled}
                        onCheckedChange={(v) => handleUpdatePreferences({...preferences, alerts_enabled: v})}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {preferences.sound_enabled ? (
                          <Volume2 className="w-5 h-5 text-slate-500" />
                        ) : (
                          <VolumeX className="w-5 h-5 text-slate-500" />
                        )}
                        <div>
                          <Label className="font-medium">Sonido</Label>
                          <p className="text-sm text-slate-500">Reproducir sonido con las notificaciones</p>
                        </div>
                      </div>
                      <Switch 
                        checked={preferences.sound_enabled}
                        onCheckedChange={(v) => handleUpdatePreferences({...preferences, sound_enabled: v})}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Categorías de Notificaciones</CardTitle>
                    <CardDescription>Elige qué tipos de notificaciones deseas recibir</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {NOTIFICATION_CATEGORIES.map(cat => (
                        <div key={cat.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div>
                            <Label className="font-medium">{cat.label}</Label>
                            <p className="text-sm text-slate-500">{cat.description}</p>
                          </div>
                          <Switch 
                            checked={preferences.categories[cat.id] !== false}
                            onCheckedChange={() => toggleCategory(cat.id)}
                            disabled={!preferences.alerts_enabled}
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Admin Tab */}
              {isAdmin && (
                <TabsContent value="admin">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <Card>
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl font-bold text-blue-600">
                          {adminStats?.total_users_subscribed || 0}
                        </div>
                        <p className="text-sm text-slate-500">Usuarios Suscritos</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl font-bold text-green-600">
                          {adminStats?.total_subscriptions || 0}
                        </div>
                        <p className="text-sm text-slate-500">Dispositivos Registrados</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <div className={`text-3xl font-bold ${adminStats?.vapid_configured ? 'text-green-600' : 'text-red-600'}`}>
                          {adminStats?.vapid_configured ? <CheckCircle className="w-8 h-8 mx-auto" /> : <XCircle className="w-8 h-8 mx-auto" />}
                        </div>
                        <p className="text-sm text-slate-500">
                          {adminStats?.vapid_configured ? "VAPID Configurado" : "VAPID No Configurado"}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Estado por Usuario */}
                  <Card className="mb-6" data-testid="push-estado-usuarios-card">
                    <CardHeader>
                      <CardTitle className="text-lg">Estado por Usuario</CardTitle>
                      <CardDescription>
                        Quién tiene las notificaciones del navegador activadas. Usa "Probar" para enviar una notificación real:
                        si llega a 1+ dispositivos y el usuario no la ve, el problema es su navegador/equipo; si llega a 0, debe activarlas.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-96">
                        <div className="space-y-2 pr-2">
                          {usuariosEstado.map((u) => (
                            <div key={u.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg" data-testid={`push-user-row-${u.id}`}>
                              <div className="flex items-center gap-3 min-w-0">
                                {u.subscribed ? (
                                  <Bell className="w-4 h-4 text-green-600 flex-shrink-0" />
                                ) : (
                                  <BellOff className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                )}
                                <div className="min-w-0">
                                  <p className="font-medium text-sm truncate">{u.name}</p>
                                  <p className="text-xs text-slate-500 truncate">{u.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {u.subscribed ? (
                                  <Badge className="bg-green-100 text-green-700 hover:bg-green-100">{u.devices} disp.</Badge>
                                ) : (
                                  <Badge variant="outline" className="border-red-300 text-red-600">Sin activar</Badge>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleTestUsuario(u)}
                                  disabled={testingUserId === u.id}
                                  data-testid={`push-test-user-btn-${u.id}`}
                                >
                                  {testingUserId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                                  Probar
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>

                  {/* Broadcast Button */}
                  <Card className="mb-6">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-lg">Enviar Notificación Masiva</h3>
                          <p className="text-slate-500">Envía una notificación a todos los usuarios suscritos</p>
                        </div>
                        <Button onClick={() => setShowBroadcastDialog(true)} className="bg-[#0C314C] text-white">
                          <Users className="w-4 h-4 mr-2" />
                          Broadcast
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Device Breakdown */}
                  {adminStats?.device_breakdown && Object.keys(adminStats.device_breakdown).length > 0 && (
                    <Card className="mb-6">
                      <CardHeader>
                        <CardTitle className="text-lg">Dispositivos por Tipo</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex gap-4">
                          {Object.entries(adminStats.device_breakdown).map(([type, count]) => (
                            <div key={type} className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-lg">
                              {type === 'mobile' ? <Smartphone className="w-5 h-5" /> : <Monitor className="w-5 h-5" />}
                              <span className="font-medium">{count}</span>
                              <span className="text-slate-500">{type === 'mobile' ? 'Móviles' : 'Escritorio'}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Recent Broadcasts */}
                  {adminStats?.recent_broadcasts?.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <History className="w-5 h-5" />
                          Broadcasts Recientes
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-64">
                          <div className="space-y-3">
                            {adminStats.recent_broadcasts.map((b, i) => (
                              <div key={i} className="p-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium">{b.payload?.title}</span>
                                  <Badge variant="outline">
                                    {b.devices_reached}/{b.target_users} dispositivos
                                  </Badge>
                                </div>
                                <p className="text-sm text-slate-600">{b.payload?.body}</p>
                                <p className="text-xs text-slate-400 mt-1">
                                  {new Date(b.created_at).toLocaleString('es-DO')} por {b.sent_by_name}
                                </p>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              )}
            </Tabs>
          )}
        </div>

        {/* Test Notification Dialog */}
        <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Enviar Notificación de Prueba</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Título</Label>
                <Input 
                  value={testMessage.title}
                  onChange={e => setTestMessage({...testMessage, title: e.target.value})}
                />
              </div>
              <div>
                <Label>Mensaje</Label>
                <Textarea 
                  value={testMessage.body}
                  onChange={e => setTestMessage({...testMessage, body: e.target.value})}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTestDialog(false)}>Cancelar</Button>
              <Button onClick={handleSendTest} className="bg-[#0C314C] text-white">
                <Send className="w-4 h-4 mr-2" />
                Enviar Prueba
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Broadcast Dialog */}
        <Dialog open={showBroadcastDialog} onOpenChange={setShowBroadcastDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Enviar Notificación Masiva</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Título *</Label>
                <Input 
                  value={broadcastMessage.title}
                  onChange={e => setBroadcastMessage({...broadcastMessage, title: e.target.value})}
                  placeholder="Título de la notificación"
                />
              </div>
              <div>
                <Label>Mensaje *</Label>
                <Textarea 
                  value={broadcastMessage.body}
                  onChange={e => setBroadcastMessage({...broadcastMessage, body: e.target.value})}
                  rows={3}
                  placeholder="Contenido del mensaje"
                />
              </div>
              <div>
                <Label>URL de destino (opcional)</Label>
                <Input 
                  value={broadcastMessage.url}
                  onChange={e => setBroadcastMessage({...broadcastMessage, url: e.target.value})}
                  placeholder="/"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBroadcastDialog(false)}>Cancelar</Button>
              <Button 
                onClick={handleBroadcast} 
                disabled={!broadcastMessage.title || !broadcastMessage.body}
                className="bg-[#0C314C] text-white"
              >
                <Users className="w-4 h-4 mr-2" />
                Enviar a Todos
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
